import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
    NavigationItemWithRoles,
} from 'app/mock-api/common/navigation/data';
import { UserProfileService } from 'app/modules/dashboard/dipita/user-profile/user-profile.service';
import { cloneDeep } from 'lodash-es';
import { from, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: NavigationItemWithRoles[] = compactNavigation;
    private readonly _defaultNavigation: NavigationItemWithRoles[] = defaultNavigation;
    private readonly _futuristicNavigation: NavigationItemWithRoles[] = futuristicNavigation;
    private readonly _horizontalNavigation: NavigationItemWithRoles[] = horizontalNavigation;

    private _userProfileService = inject(UserProfileService);

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Filter navigation items based on user roles
     */
    private async filterNavigationByRoles(navigation: NavigationItemWithRoles[]): Promise<FuseNavigationItem[]> {
        // Make sure the profile is loaded to have the roles
        const clinicianProfile = this._userProfileService.clinicianProfile();
        if (!clinicianProfile) {
            try {
                await this._userProfileService.getProfile();

            } catch (error) {
                console.warn('Could not load profile for navigation filtering:', error);
            }
        }

        const userRoles = this._userProfileService.userRoles();
        // console.log('User roles for navigation:', userRoles);
        
        return navigation
            .filter(item => {
                // If item doesn't have requires role, it's visible for all
                if (!item.roles || item.roles.length === 0) {
                    return true;
                }

                const hasRole = item.roles.some(requiredRole => userRoles.includes(requiredRole));
                // console.log(`Item "${item.title}" requires ${item.roles}, user has access: ${hasRole}`);
                
                return hasRole;
            })
            .map(item => {
                // Delete roles property before returning item
                const { roles, ...fuseNavItem } = item;
                return fuseNavItem as FuseNavigationItem;
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            return from(this.getFilteredNavigation()).pipe(
                map(result => [200, result])
            );
        });
    }

    /**
     * Get filtered navigation data
     */
    private async getFilteredNavigation(): Promise<any> {
        // Filter navigation based on user roles
        const filteredDefaultNavigation = await this.filterNavigationByRoles(this._defaultNavigation);
        const filteredCompactNavigation = await this.filterNavigationByRoles(this._compactNavigation);
        const filteredFuturisticNavigation = await this.filterNavigationByRoles(this._futuristicNavigation);
        const filteredHorizontalNavigation = await this.filterNavigationByRoles(this._horizontalNavigation);

        // Fill compact navigation children using the filtered default navigation
        filteredCompactNavigation.forEach((compactNavItem) => {
            filteredDefaultNavigation.forEach((defaultNavItem) => {
                if (defaultNavItem.id === compactNavItem.id) {
                    compactNavItem.children = cloneDeep(defaultNavItem.children);
                }
            });
        });

        // Fill futuristic navigation children using the filtered default navigation
        filteredFuturisticNavigation.forEach((futuristicNavItem) => {
            filteredDefaultNavigation.forEach((defaultNavItem) => {
                if (defaultNavItem.id === futuristicNavItem.id) {
                    futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                }
            });
        });

        // Fill horizontal navigation children using the filtered default navigation
        filteredHorizontalNavigation.forEach((horizontalNavItem) => {
            filteredDefaultNavigation.forEach((defaultNavItem) => {
                if (defaultNavItem.id === horizontalNavItem.id) {
                    horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                }
            });
        });

        // Return the response with filtered navigation
        return {
            compact: cloneDeep(filteredCompactNavigation),
            default: cloneDeep(filteredDefaultNavigation),
            futuristic: cloneDeep(filteredFuturisticNavigation),
            horizontal: cloneDeep(filteredHorizontalNavigation),
        };
    }
}